import { useState, useEffect } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { authClient } from "@/lib/auth-client";
import { useProfile } from "@/hooks/useProfile";

interface AccountInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onManageVoice: () => void;
}

const AccountInfoModal = ({
  isOpen,
  onClose,
  onManageVoice,
}: AccountInfoModalProps) => {
  const { user, setAuth, token } = useAuthStore();

  // Only fetch the enriched profile (org-admin contact + co-teachers) while the
  // modal is open — it's net-new data not carried in the Better Auth session.
  const { profile } = useProfile(isOpen);
  const orgAdminContact = profile?.org_admin_contact;
  const coteachers = profile?.coteachers ?? [];

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [originalFirstName, setOriginalFirstName] = useState("");
  const [originalLastName, setOriginalLastName] = useState("");
  const [originalDisplayName, setOriginalDisplayName] = useState("");
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [roleError, setRoleError] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const hasChanges =
    firstName !== originalFirstName ||
    lastName !== originalLastName ||
    displayName !== originalDisplayName;

  // Reset UI state only when the modal opens — not on every user-data update.
  // Keeping setSaveSuccess here would cause the "Changes saved" banner to vanish
  // immediately after save because setAuth() triggers a user.name update which
  // re-fires the combined effect before the 3-second auto-hide timer fires.
  useEffect(() => {
    if (isOpen) {
      setSaveSuccess(false);
      setSaveError(null);
      setIsSaving(false);
      setFirstNameError(false);
      setLastNameError(false);
      setEmailError(false);
      setRoleError(false);
    }
  }, [isOpen]);

  // Populate name fields when modal opens or user data changes externally.
  useEffect(() => {
    if (isOpen && user?.name) {
      const nameParts = user.name.split(" ");
      const first = nameParts[0] || "";
      const last = nameParts.slice(1).join(" ") || "";
      const display = user.displayUsername || "";

      setFirstName(first);
      setLastName(last);
      setDisplayName(display);
      setOriginalFirstName(first);
      setOriginalLastName(last);
      setOriginalDisplayName(display);
    }
  }, [isOpen, user?.displayUsername, user?.id, user?.name]);

  // Auto-hide email error after 3 seconds
  useEffect(() => {
    if (emailError) {
      const timer = setTimeout(() => setEmailError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [emailError]);

  // Auto-hide role error after 3 seconds
  useEffect(() => {
    if (roleError) {
      const timer = setTimeout(() => setRoleError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [roleError]);

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  const handleSaveChanges = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Validate required fields (only first name is required)
    const isFirstNameEmpty = !firstName.trim();

    setFirstNameError(isFirstNameEmpty);

    if (isFirstNameEmpty) {
      return;
    }

    if (!hasChanges || isSaving) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      const fullName = lastName.trim()
        ? `${firstName.trim()} ${lastName.trim()}`
        : firstName.trim();
      const trimmedDisplayName = displayName.trim();
      const { error } = await authClient.updateUser({
        name: fullName,
        ...(user?.username ? { username: user.username } : {}),
        displayUsername: trimmedDisplayName || undefined,
      });

      if (error) {
        console.error("Error saving changes:", error);
        setSaveError("Your changes could not be saved. Please try again.");
        setIsSaving(false);
        return;
      }

      // Update local auth store
      if (user) {
        setAuth(token || "", {
          ...user,
          name: fullName,
          displayUsername: trimmedDisplayName || undefined,
        });
      }

      setSaveSuccess(true);
      setOriginalFirstName(firstName.trim());
      setOriginalLastName(lastName.trim());
      setOriginalDisplayName(trimmedDisplayName);
      setFirstName(firstName.trim());
      setLastName(lastName.trim());
      setDisplayName(trimmedDisplayName);
    } catch (error) {
      console.error("Error saving changes:", error);
      setSaveError("Your changes could not be saved. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div>
      <div className="et-settings-form">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-nunito font-extrabold text-[24px] text-[#294B39]">
            Account Information
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSaveChanges} className="et-account-fields">
          {saveError && (
            <p role="alert" className="text-sm text-red-600">
              {saveError}
            </p>
          )}
          {/* First Name & Last Name */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block font-nunito font-medium text-[16px] leading-none text-gray-500 mb-2">
                First Name
              </label>
              <input
                type="text"
                aria-label="First name"
                aria-invalid={firstNameError}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (firstNameError) setFirstNameError(false);
                }}
                className={`w-full h-[56px] py-[10px] px-[18px] bg-white border rounded-[12px] font-mulish font-medium text-[20px] leading-none text-[#294B39] focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D] ${
                  firstNameError ? "border-[#F87171]" : "border-[#E0E0E0]"
                }`}
              />
              {firstNameError && (
                <p className="mt-2 font-mulish font-bold text-[15px] leading-none text-[#F87171]">
                  First name is required.
                </p>
              )}
            </div>
            <div className="flex-1">
              <label className="block font-nunito font-medium text-[16px] leading-none text-gray-500 mb-2">
                Last Name
              </label>
              <input
                type="text"
                aria-label="Last name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (lastNameError) setLastNameError(false);
                }}
                className="w-full h-[56px] py-[10px] px-[18px] bg-white border border-[#E0E0E0] rounded-[12px] font-mulish font-medium text-[20px] leading-none text-[#294B39] focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D]"
              />
            </div>
          </div>

          {/* Display Username */}
          <div>
            <label className="block font-nunito font-medium text-[16px] leading-none text-gray-500 mb-2">
              Display Name (Optional)
            </label>
            <input
              type="text"
              aria-label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full h-[56px] py-[10px] px-[18px] bg-white border border-[#E0E0E0] rounded-[12px] font-mulish font-medium text-[20px] leading-none text-[#294B39] focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D]"
            />
          </div>

          {/* Email ID (Read-only) */}
          <div>
            <label className="block font-nunito font-medium text-[16px] leading-none text-gray-500 mb-2">
              Email ID
            </label>
            <div
              className="relative cursor-pointer"
              onClick={() => setEmailError(true)}
            >
              <input
                type="email"
                aria-label="Email ID"
                value={user?.email || ""}
                readOnly
                className="w-full h-[56px] py-[10px] px-[18px] pr-12 rounded-[12px] font-mulish font-medium text-[20px] leading-none text-[#294B39] bg-[#F4F4F4] cursor-not-allowed pointer-events-none"
              />
              <div className="absolute inset-y-0 right-4 flex items-center">
                <svg
                  width="16"
                  height="20"
                  viewBox="0 0 14 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.77206 0C3.95037 0 1.69301 2.25735 1.69301 5.07904V7.3364C0.73364 7.3364 0 8.07004 0 9.02941V16.3658C0 17.3252 0.73364 18.0588 1.69301 18.0588H11.8511C12.8105 18.0588 13.5441 17.3252 13.5441 16.3658V9.02941C13.5441 8.07004 12.8105 7.3364 11.8511 7.3364V5.07904C11.8511 2.25735 9.59375 0 6.77206 0ZM12.4154 9.02941V16.3658C12.4154 16.7044 12.1897 16.9301 11.8511 16.9301H1.69301C1.35441 16.9301 1.12868 16.7044 1.12868 16.3658V9.02941C1.12868 8.69081 1.35441 8.46507 1.69301 8.46507H2.25735H11.2868H11.8511C12.1897 8.46507 12.4154 8.69081 12.4154 9.02941ZM2.82169 7.3364V5.07904C2.82169 2.87812 4.57114 1.12868 6.77206 1.12868C8.97298 1.12868 10.7224 2.87812 10.7224 5.07904V7.3364H2.82169Z"
                    fill="#28543D"
                  />
                  <path
                    d="M6.77114 10.1581C5.81176 10.1581 5.07812 10.8917 5.07812 11.8511C5.07812 12.5847 5.5296 13.2055 6.2068 13.4312V14.6728C6.2068 15.0114 6.43254 15.2371 6.77114 15.2371C7.10974 15.2371 7.33548 15.0114 7.33548 14.6728V13.4312C8.01268 13.2055 8.46415 12.5847 8.46415 11.8511C8.46415 10.8917 7.73051 10.1581 6.77114 10.1581ZM6.77114 12.4154C6.43254 12.4154 6.2068 12.1897 6.2068 11.8511C6.2068 11.5125 6.43254 11.2868 6.77114 11.2868C7.10974 11.2868 7.33548 11.5125 7.33548 11.8511C7.33548 12.1897 7.10974 12.4154 6.77114 12.4154Z"
                    fill="#28543D"
                  />
                </svg>
              </div>
            </div>
            {emailError && (
              <p className="mt-2 font-mulish font-semibold text-[15px] leading-normal text-[#F87171]">
                Email is view-only. Please contact{" "}
                <span className="font-bold">support@evertutor.ai</span>
                <br />
                for changes.
              </p>
            )}
          </div>

          {/* Role (Read-only) */}
          <div className="et-account-role">
            <label className="block font-nunito font-medium text-[16px] leading-none text-gray-500 mb-2">
              Role
            </label>
            <div
              className="relative cursor-pointer"
              onClick={() => setRoleError(true)}
            >
              <input
                type="text"
                aria-label="Role"
                value={
                  user?.role === "TEACHER"
                    ? "Teacher"
                    : user?.role === "STUDENT"
                      ? "Student"
                      : user?.role || ""
                }
                readOnly
                className="w-full h-[56px] py-[10px] px-[18px] pr-12 rounded-[12px] font-mulish font-medium text-[20px] leading-none text-[#294B39] bg-[#F4F4F4] cursor-not-allowed pointer-events-none"
              />
              <div className="absolute inset-y-0 right-4 flex items-center">
                <svg
                  width="16"
                  height="20"
                  viewBox="0 0 14 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.77206 0C3.95037 0 1.69301 2.25735 1.69301 5.07904V7.3364C0.73364 7.3364 0 8.07004 0 9.02941V16.3658C0 17.3252 0.73364 18.0588 1.69301 18.0588H11.8511C12.8105 18.0588 13.5441 17.3252 13.5441 16.3658V9.02941C13.5441 8.07004 12.8105 7.3364 11.8511 7.3364V5.07904C11.8511 2.25735 9.59375 0 6.77206 0ZM12.4154 9.02941V16.3658C12.4154 16.7044 12.1897 16.9301 11.8511 16.9301H1.69301C1.35441 16.9301 1.12868 16.7044 1.12868 16.3658V9.02941C1.12868 8.69081 1.35441 8.46507 1.69301 8.46507H2.25735H11.2868H11.8511C12.1897 8.46507 12.4154 8.69081 12.4154 9.02941ZM2.82169 7.3364V5.07904C2.82169 2.87812 4.57114 1.12868 6.77206 1.12868C8.97298 1.12868 10.7224 2.87812 10.7224 5.07904V7.3364H2.82169Z"
                    fill="#28543D"
                  />
                  <path
                    d="M6.77114 10.1581C5.81176 10.1581 5.07812 10.8917 5.07812 11.8511C5.07812 12.5847 5.5296 13.2055 6.2068 13.4312V14.6728C6.2068 15.0114 6.43254 15.2371 6.77114 15.2371C7.10974 15.2371 7.33548 15.0114 7.33548 14.6728V13.4312C8.01268 13.2055 8.46415 12.5847 8.46415 11.8511C8.46415 10.8917 7.73051 10.1581 6.77114 10.1581ZM6.77114 12.4154C6.43254 12.4154 6.2068 12.1897 6.2068 11.8511C6.2068 11.5125 6.43254 11.2868 6.77114 11.2868C7.10974 11.2868 7.33548 11.5125 7.33548 11.8511C7.33548 12.1897 7.10974 12.4154 6.77114 12.4154Z"
                    fill="#28543D"
                  />
                </svg>
              </div>
            </div>
            {roleError && (
              <p className="mt-2 font-mulish font-semibold text-[15px] leading-normal text-[#F87171]">
                Role is view-only. Please contact{" "}
                <span className="font-bold">support@evertutor.ai</span>
                <br />
                for changes.
              </p>
            )}
          </div>

          {/* Org admin contact (read-only) — who manages this account. */}
          {orgAdminContact &&
            (orgAdminContact.name || orgAdminContact.email) && (
              <div className="et-account-organization">
                <label className="block font-nunito font-medium text-[16px] leading-none text-gray-500 mb-2">
                  Organization Admin
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={
                      orgAdminContact.name && orgAdminContact.email
                        ? `${orgAdminContact.name} · ${orgAdminContact.email}`
                        : orgAdminContact.name || orgAdminContact.email || ""
                    }
                    readOnly
                    className="w-full h-[56px] py-[10px] px-[18px] pr-12 rounded-[12px] font-mulish font-medium text-[18px] leading-none text-[#294B39] bg-[#F4F4F4] cursor-not-allowed truncate"
                  />
                  <div className="absolute inset-y-0 right-4 flex items-center">
                    <svg
                      width="16"
                      height="20"
                      viewBox="0 0 14 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.77206 0C3.95037 0 1.69301 2.25735 1.69301 5.07904V7.3364C0.73364 7.3364 0 8.07004 0 9.02941V16.3658C0 17.3252 0.73364 18.0588 1.69301 18.0588H11.8511C12.8105 18.0588 13.5441 17.3252 13.5441 16.3658V9.02941C13.5441 8.07004 12.8105 7.3364 11.8511 7.3364V5.07904C11.8511 2.25735 9.59375 0 6.77206 0ZM12.4154 9.02941V16.3658C12.4154 16.7044 12.1897 16.9301 11.8511 16.9301H1.69301C1.35441 16.9301 1.12868 16.7044 1.12868 16.3658V9.02941C1.12868 8.69081 1.35441 8.46507 1.69301 8.46507H2.25735H11.2868H11.8511C12.1897 8.46507 12.4154 8.69081 12.4154 9.02941ZM2.82169 7.3364V5.07904C2.82169 2.87812 4.57114 1.12868 6.77206 1.12868C8.97298 1.12868 10.7224 2.87812 10.7224 5.07904V7.3364H2.82169Z"
                        fill="#28543D"
                      />
                      <path
                        d="M6.77114 10.1581C5.81176 10.1581 5.07812 10.8917 5.07812 11.8511C5.07812 12.5847 5.5296 13.2055 6.2068 13.4312V14.6728C6.2068 15.0114 6.43254 15.2371 6.77114 15.2371C7.10974 15.2371 7.33548 15.0114 7.33548 14.6728V13.4312C8.01268 13.2055 8.46415 12.5847 8.46415 11.8511C8.46415 10.8917 7.73051 10.1581 6.77114 10.1581ZM6.77114 12.4154C6.43254 12.4154 6.2068 12.1897 6.2068 11.8511C6.2068 11.5125 6.43254 11.2868 6.77114 11.2868C7.10974 11.2868 7.33548 11.5125 7.33548 11.8511C7.33548 12.1897 7.10974 12.4154 6.77114 12.4154Z"
                        fill="#28543D"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            )}

          {/* Co-teachers (read-only) — teachers this account may view-as. */}
          {coteachers.length > 0 && (
            <div>
              <label className="block font-nunito font-medium text-[16px] leading-none text-gray-500 mb-2">
                Co-teaching for
              </label>
              <div className="flex flex-col gap-2">
                {coteachers.map((ct) => (
                  <div
                    key={ct.teacher_id}
                    className="flex flex-col rounded-[12px] bg-[#F4F4F4] py-[10px] px-[18px]"
                  >
                    <span className="font-mulish font-medium text-[16px] leading-tight text-[#294B39] truncate">
                      {ct.name || ct.email || `Teacher ${ct.teacher_id}`}
                    </span>
                    {ct.name && ct.email && (
                      <span className="font-mulish text-[13px] leading-tight text-gray-400 truncate">
                        {ct.email}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </form>

        {/* Voice & pronunciation section */}
        <div className="et-voice-link-card border border-[#E5E5E5] rounded-[10px] p-4 mt-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-nunito font-bold text-[15px] text-[#252525]">
                Voice &amp; pronunciation
              </p>
              <p className="font-mulish text-[13px] text-[#7D7D7D] mt-0.5 leading-snug">
                These settings apply to every lesson. You can change them
                in-lesson too — your profile updates automatically.
              </p>
            </div>
            <button
              type="button"
              onClick={onManageVoice}
              className="flex-shrink-0 h-[44px] px-4 bg-[#F0FAF5] text-[#28543D] font-nunito font-bold text-[13px] rounded-[10px] hover:bg-[#D8F3E8] transition-colors"
            >
              Manage
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="et-settings-actions flex items-center justify-center gap-4 mt-6">
          {isSaving ? (
            <div className="flex items-center justify-center h-[57px] px-6 border border-[#F5F5F5] rounded-[10px] gap-[10px]">
              <svg
                className="animate-spin"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 14C3.143 14 0 10.857 0 7C0 5.691 0.364 4.417 1.05 3.311C1.253 2.982 1.687 2.884 2.016 3.087C2.345 3.29 2.443 3.72399 2.24 4.05299C1.694 4.93499 1.4 5.957 1.4 7C1.4 10.087 3.913 12.6 7 12.6C10.087 12.6 12.6 10.087 12.6 7C12.6 3.913 10.087 1.4 7 1.4C6.615 1.4 6.3 1.085 6.3 0.7C6.3 0.315 6.615 0 7 0C10.857 0 14 3.143 14 7C14 10.857 10.857 14 7 14Z"
                  fill="black"
                />
              </svg>
              <span className="font-mulish font-bold text-[15px] leading-none text-gray-900">
                Saving...
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={!hasChanges}
              className={`h-[56px] py-[14px] px-[24px] font-nunito font-bold text-[20px] leading-none rounded-[12px] transition-colors ${
                hasChanges
                  ? "bg-[#28543D] text-white hover:bg-[#28543D]/90"
                  : "bg-[#CFE9DF] text-white cursor-not-allowed"
              }`}
            >
              Save Changes
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className={`font-nunito font-semibold text-[16px] transition-colors ${
              isSaving
                ? "text-[#28543D]/50 cursor-not-allowed"
                : "text-[#28543D] hover:text-[#28543D]/80"
            }`}
          >
            Cancel
          </button>
        </div>

        {saveSuccess && (
          <div className="flex items-center justify-center gap-2 mt-3">
            <span className="flex h-5 w-5 items-center justify-center rounded-[4px]">
              <svg
                width="12"
                height="10"
                viewBox="0 0 12 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 5L4.5 8.5L11 1.5"
                  stroke="#28543D"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="font-nunito font-semibold text-[14px] leading-none text-[#28543D]">
              Changes saved.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountInfoModal;
